<template>
  <div class="co-lead-financial-summary">
    <p class="co-lead-financial-summary__heading">Financial Summary</p>

    <b-row class="row">
      <b-col lg="4">
        <div class="co-lead-financial-summary__box">
          <div class="co-lead-financial-summary__box-label">Total Cash Call Amount</div>
          <div class="co-lead-financial-summary__box-value">{{ formatMoney(totalCashCallAmount) }}</div>
        </div>
      </b-col>
      <b-col lg="5">
        <div class="co-lead-financial-summary__box co-lead-financial-summary__box--highlight">
          <div class="co-lead-financial-summary__box-label">Total Cash Call Required</div>
          <div class="co-lead-financial-summary__box-value">{{ formatMoney(totalRequired) }}</div>
        </div>
      </b-col>
    </b-row>

    <b-table-simple small class="mt-3">
      <b-thead>
        <b-tr>
          <b-th>UMR</b-th>
          <b-th>Underwriter</b-th>
          <b-th>Share %</b-th>
          <b-th>Amount</b-th>
          <b-th>Required</b-th>
          <b-th>Funding req.</b-th>
        </b-tr>
      </b-thead>
      <b-tbody>
        <b-tr v-for="row in summaryRows" :key="row.key" :class="{ 'co-lead-financial-summary__row--no-funding': !row.fundingRequired }">
          <b-td>{{ row.umr }}</b-td>
          <b-td>{{ row.underwriter }}</b-td>
          <b-td>{{ row.sharePercentage }}%</b-td>
          <b-td>{{ formatMoney(row.amount) }}</b-td>
          <b-td>{{ row.fundingRequired ? formatMoney(row.amount) : '—' }}</b-td>
          <b-td>
            <span class="co-lead-financial-summary__badge" :class="{ 'co-lead-financial-summary__badge--no': !row.fundingRequired }">
              {{ row.fundingRequired ? 'Yes' : 'No' }}
            </span>
          </b-td>
        </b-tr>
        <b-tr class="co-lead-financial-summary__total-row">
          <b-td colspan="2"><strong>TOTAL</strong></b-td>
          <b-td><strong>{{ totalSharePercentage }}%</strong></b-td>
          <b-td><strong>{{ formatMoney(totalAmount) }}</strong></b-td>
          <b-td><strong>{{ formatMoney(totalRequired) }}</strong></b-td>
          <b-td></b-td>
        </b-tr>
      </b-tbody>
    </b-table-simple>
  </div>
</template>

<script>
import { defineComponent, computed } from '@vue/composition-api'

export default defineComponent({
  name: 'CashCallCoLeadFinancialSummary',
  props: {
    rows: {
      type: Array,
      default: () => []
    },
    totalCashCallAmount: {
      type: Number,
      default: 0
    }
  },
  setup(props) {
    const summaryRows = computed(() => props.rows.map((row) => {
      const sharePercentage = Number(row.share_percentage) || 0
      const amount = Math.round(props.totalCashCallAmount * sharePercentage) / 100
      return {
        key: row.bdx_key[0],
        umr: row.umr,
        underwriter: row.underwriter_lead,
        sharePercentage,
        amount,
        fundingRequired: row.funding_required !== false
      }
    }))

    const totalSharePercentage = computed(() =>
      Math.round(summaryRows.value.reduce((sum, row) => sum + row.sharePercentage, 0) * 100) / 100
    )
    const totalAmount = computed(() =>
      Math.round(summaryRows.value.reduce((sum, row) => sum + row.amount, 0) * 100) / 100
    )
    const totalRequired = computed(() =>
      Math.round(summaryRows.value.filter((row) => row.fundingRequired).reduce((sum, row) => sum + row.amount, 0) * 100) / 100
    )

    const formatMoney = (value) => {
      const number = Number(value) || 0
      return number.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }

    return {
      summaryRows,
      totalSharePercentage,
      totalAmount,
      totalRequired,
      formatMoney
    }
  }
})
</script>

<style scoped>
.co-lead-financial-summary__heading {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #8898aa;
  margin-bottom: 10px;
}

.co-lead-financial-summary__box {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 14px 16px;
}

.co-lead-financial-summary__box--highlight {
  background-color: #1c4a50;
  color: #fff;
}

.co-lead-financial-summary__box-label {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.8;
}

.co-lead-financial-summary__box-value {
  font-size: 1.5rem;
  font-weight: 700;
}

.co-lead-financial-summary__badge {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  /* Match the "Total Cash Call Required" box - filled teal (#1c4a50) with white
     text - so the "Yes" funding badge reads as the same brand colour. */
  background-color: #1c4a50;
  color: #fff;
}

.co-lead-financial-summary__badge--no {
  background-color: #fde8ec;
  color: #f5365c;
}

.co-lead-financial-summary__total-row {
  background-color: #f8f9fa;
}

/* Funding-off rows still count toward the 100% share allocation, but are not
   funded (Required shows "—") - grey them out so it's clear at a glance they're
   excluded from the funding/exports. The badge keeps its own red "No" colour. */
.co-lead-financial-summary__row--no-funding td:not(:last-child) {
  color: #adb5bd;
  background-color: #fafbfc;
}
</style>
